import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ShieldCheck, UsersRound, Bell, LifeBuoy, FileWarning, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Page, Spinner, StatusPill, severityMeta } from "@/components/shared";

export default function Dashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/band/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner label="Loading command centre…" />;

  const m = d.metrics;
  const cards = [
    { icon: Users, label: "Registered Participants", value: m.participants, color: "var(--cs-magenta)" },
    { icon: ShieldCheck, label: "Passports Complete", value: `${m.passports_complete} (${m.passport_pct}%)`, color: "var(--cs-green)" },
    { icon: UsersRound, label: "Crew Groups", value: m.crews, color: "var(--cs-blue)" },
    { icon: Bell, label: "Active Alerts", value: m.active_alerts, color: "var(--cs-amber)" },
    { icon: LifeBuoy, label: "Open Help Requests", value: m.open_help, color: m.open_help > 0 ? "var(--cs-red)" : "var(--cs-green)" },
    { icon: FileWarning, label: "Incidents", value: m.incidents, color: "var(--cs-amber)" },
  ];

  return (
    <Page>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-head font-extrabold text-3xl">{d.band.name}</h1>
          <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Band Code: <span className="font-mono" style={{ color: "var(--cs-magenta)" }}>{d.band.code}</span> · Road Day {d.band.road_day_active ? "ACTIVE" : "off"}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`leader-metric-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="cs-card p-5">
            <c.icon size={22} style={{ color: c.color }} />
            <p className="font-head font-extrabold text-3xl mt-3">{c.value}</p>
            <p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="cs-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} style={{ color: "var(--cs-red)" }} />
            <h2 className="font-head font-bold text-lg">Outstanding Help Requests</h2>
          </div>
          {d.open_help.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--cs-green)" }}>All clear — no open requests.</p>
          ) : (
            <div className="space-y-2">
              {d.open_help.map((h) => (
                <button key={h.id} data-testid="command-center-triage-item" onClick={() => nav("/leader/help")}
                  className="cs-tap w-full text-left rounded-xl p-3 flex items-center justify-between"
                  style={{ background: "var(--cs-card-hi)", border: `1px solid ${h.status === "Requested" ? "var(--cs-red)" : "var(--cs-border)"}` }}>
                  <div>
                    <p className="font-semibold text-sm">{h.type}</p>
                    <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{h.user_name}</p>
                  </div>
                  <StatusPill status={h.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="cs-card p-5">
          <h2 className="font-head font-bold text-lg mb-4">Recent Communications</h2>
          <div className="space-y-3">
            {d.recent_updates.map((u) => {
              const sm = severityMeta(u.severity);
              return (
                <div key={u.id} className="flex gap-3">
                  <span className="rounded-full flex-shrink-0 mt-1.5" style={{ width: 9, height: 9, background: sm.color }} />
                  <div>
                    <p className="font-semibold text-sm">{u.title}</p>
                    <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{u.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <button data-testid="leader-goto-comms" onClick={() => nav("/leader/communications")} className="cs-tap mt-4 text-sm font-semibold" style={{ color: "var(--cs-magenta)" }}>Send an update →</button>
        </div>
      </div>
    </Page>
  );
}
